import {
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import TournamentSetupShieldPicker from './TournamentSetupShieldPicker';
import GroupColorPicker from './GroupColorPicker';

/** Pestaña «Grupos y equipos»: bolsa, crear grupo, tarjetas por grupo. */
export default function TournamentSetupGroupsTab({
  availableTeams,
  teams,
  groups,
  busy,
  poolTeamName,
  setPoolTeamName,
  poolTeamShield,
  setPoolTeamShield,
  newGroupName,
  setNewGroupName,
  newTeamInGroupDraft,
  shieldByGroup,
  addMenu,
  setAddMenu,
  closeAddMenu,
  setDraftForGroup,
  setShieldForGroup,
  handleCreatePoolTeam,
  handleCreateGroup,
  handleDeleteGroup,
  handleCreateTeamInGroup,
  handleAddTeamToGroup,
  handleRemoveTeamFromGroup,
  handleRemoveTeamFromTournament,
  handleSetGroupColor,
  onFileTooLargeShield,
  openTeamLibrary,
  onPickFromLibraryForPool,
  onPickFromLibraryForGroup,
}) {
  return (
    <>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0f3531', mb: 1 }}>
        Bolsa de equipos (sin grupo aún)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Estos equipos ya están en el torneo pero no en ningún grupo. Así puedes prepararlos todos y repartirlos después.
        Imágenes JPG/PNG/WebP hasta ~1,5&nbsp;MB.
      </Typography>
      {availableTeams.length > 0 ? (
        <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
          {availableTeams.map((t) => {
            const chip = (
              <Chip
                key={t.id}
                avatar={<Avatar src={t.shield || undefined} alt="">{!t.shield ? (t.name || '?').charAt(0) : null}</Avatar>}
                label={t.name}
                variant="outlined"
                onDelete={
                  handleRemoveTeamFromTournament
                    ? () => handleRemoveTeamFromTournament(t.id, t.name)
                    : undefined
                }
                disabled={busy}
                sx={{ borderColor: 'rgba(15,53,49,0.2)', fontWeight: 600 }}
              />
            );
            return handleRemoveTeamFromTournament ? (
              <Tooltip key={t.id} title="Quitar del torneo (solo si no está en grupos ni en partidos)">
                {chip}
              </Tooltip>
            ) : (
              chip
            );
          })}
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Nadie en la bolsa todavía. Crea equipos con el formulario de abajo.
        </Typography>
      )}

      <Box
        component="form"
        onSubmit={handleCreatePoolTeam}
        sx={{
          mb: 3,
          p: 2,
          borderRadius: 2,
          bgcolor: 'rgba(201,162,39,0.08)',
          border: '1px solid rgba(201,162,39,0.25)',
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 1 }}>
          Nuevo equipo en el torneo (sin meter en grupo)
        </Typography>
        <TournamentSetupShieldPicker
          value={poolTeamShield}
          onChange={setPoolTeamShield}
          disabled={busy}
          onFileTooLarge={onFileTooLargeShield}
        />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 1.5 }} alignItems={{ sm: 'center' }}>
          <TextField
            size="small"
            label="Nombre del equipo"
            value={poolTeamName}
            onChange={(e) => setPoolTeamName(e.target.value)}
            disabled={busy}
            sx={{ flex: 1, minWidth: 200 }}
          />
          <Button
            type="submit"
            variant="contained"
            disabled={busy || !poolTeamName.trim()}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              bgcolor: '#c9a227',
              color: '#1a1a1a',
              '&:hover': { bgcolor: '#b89220' },
            }}
          >
            Dar de alta en la bolsa
          </Button>
        </Stack>
        {openTeamLibrary && onPickFromLibraryForPool ? (
          <Button
            type="button"
            variant="outlined"
            size="small"
            startIcon={<BookmarkBorderIcon />}
            disabled={busy}
            onClick={() => openTeamLibrary(onPickFromLibraryForPool)}
            sx={{
              mt: 1.5,
              textTransform: 'none',
              fontWeight: 700,
              borderColor: 'rgba(15,53,49,0.3)',
              color: '#0f3531',
              '&:hover': { borderColor: '#0f6b5c', bgcolor: 'rgba(15,107,92,0.06)' },
            }}
          >
            Elegir de mi biblioteca
          </Button>
        ) : null}
      </Box>

      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0f3531', mb: 1 }}>
        Grupos
      </Typography>
      <Box
        component="form"
        onSubmit={handleCreateGroup}
        sx={{
          display: 'flex',
          gap: 1.5,
          flexWrap: 'wrap',
          alignItems: 'flex-start',
          mb: 2.5,
          p: 2,
          borderRadius: 2,
          bgcolor: 'rgba(15,107,92,0.06)',
          border: '1px solid rgba(15,107,92,0.12)',
        }}
      >
        <TextField
          size="small"
          label="Nombre del grupo"
          value={newGroupName}
          onChange={(e) => setNewGroupName(e.target.value)}
          sx={{ minWidth: 200, flex: '1 1 200px' }}
          disabled={busy}
        />
        <Button
          type="submit"
          variant="contained"
          disabled={busy || !newGroupName.trim()}
          sx={{
            mt: { xs: 0, sm: '2px' },
            px: 2.5,
            py: 1,
            fontWeight: 700,
            textTransform: 'none',
            bgcolor: '#0f6b5c',
            boxShadow: '0 2px 8px rgba(15,107,92,0.35)',
            '&:hover': { bgcolor: '#0c5a4e' },
            '&.Mui-disabled': { bgcolor: 'action.disabledBackground', color: 'action.disabled' },
          }}
        >
          Crear grupo
        </Button>
        {!newGroupName.trim() && (
          <Typography variant="caption" sx={{ width: '100%', color: 'text.secondary', mt: -0.5 }}>
            Escribe un nombre para activar el botón.
          </Typography>
        )}
      </Box>
      <Stack spacing={2}>
        {groups.map((g) => {
          const gTeams = g.tournamentGroupTeams || [];
          const draft = newTeamInGroupDraft[g.id] ?? '';
          const groupShield = shieldByGroup[g.id] || '';
          return (
            <Paper
              key={g.id}
              elevation={0}
              sx={{
                borderRadius: 2.5,
                border: '1px solid rgba(15,53,49,0.12)',
                overflow: 'hidden',
                boxShadow: '0 4px 20px rgba(15,53,49,0.06)',
              }}
            >
              <Box
                sx={{
                  px: 2,
                  py: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  bgcolor: 'rgba(15,53,49,0.04)',
                  borderBottom: '1px solid rgba(15,53,49,0.08)',
                }}
              >
                <Stack direction="row" alignItems="center" gap={1} minWidth={0}>
                  {g.color ? (
                    <Box
                      aria-hidden
                      sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: g.color, border: '1px solid rgba(0,0,0,0.1)' }}
                    />
                  ) : (
                    <PeopleOutlineIcon sx={{ color: '#0f6b5c', fontSize: 22 }} />
                  )}
                  <Typography variant="subtitle1" fontWeight={700} noWrap sx={{ color: '#0f3531' }}>
                    {g.name}
                  </Typography>
                  <Chip
                    size="small"
                    label={`${gTeams.length} equipos`}
                    sx={{ bgcolor: 'rgba(201,162,39,0.2)', color: '#5c4a10', fontWeight: 600 }}
                  />
                </Stack>
                <Stack direction="row" alignItems="center" gap={0.5}>
                  {handleSetGroupColor ? (
                    <GroupColorPicker
                      value={g.color || ''}
                      onChange={(c) => handleSetGroupColor(g.id, g.name, c)}
                      disabled={busy}
                      label={`Color del grupo ${g.name}`}
                    />
                  ) : null}
                  <IconButton
                    aria-label="Eliminar grupo"
                    size="small"
                    onClick={() => handleDeleteGroup(g.id, g.name)}
                    sx={{ color: 'text.secondary', '&:hover': { color: 'error.main', bgcolor: 'rgba(211,47,47,0.08)' } }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Box>
              <Box sx={{ p: 2 }}>
                {gTeams.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Aún no hay equipos. Crea uno nuevo abajo o añade uno que ya exista en el torneo.
                  </Typography>
                ) : (
                  <Stack spacing={0.75} sx={{ mb: 2 }}>
                    {gTeams.map((gt) => (
                      <Stack
                        key={gt.id}
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        spacing={1}
                        sx={{
                          py: 0.75,
                          px: 1.25,
                          borderRadius: 1.5,
                          bgcolor: 'rgba(0,0,0,0.03)',
                        }}
                      >
                        <Typography variant="body2" fontWeight={500}>
                          {gt.team?.name || String(gt.teamId).slice(0, 8)}
                        </Typography>
                        <Button
                          size="small"
                          onClick={() => handleRemoveTeamFromGroup(gt.teamId)}
                          disabled={busy}
                          sx={{ textTransform: 'none', minWidth: 0, color: 'text.secondary' }}
                        >
                          Quitar
                        </Button>
                      </Stack>
                    ))}
                  </Stack>
                )}

                <Divider sx={{ my: 1.5 }} />

                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 1 }}>
                  Nuevo equipo en este grupo
                </Typography>
                <Box sx={{ mb: 1.5 }}>
                  <TournamentSetupShieldPicker
                    value={groupShield}
                    onChange={(v) => setShieldForGroup(g.id, v)}
                    disabled={busy}
                    onFileTooLarge={onFileTooLargeShield}
                  />
                </Box>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Ej. CD Ejemplo"
                    value={draft}
                    onChange={(e) => setDraftForGroup(g.id, e.target.value)}
                    disabled={busy}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleCreateTeamInGroup(g.id);
                      }
                    }}
                  />
                  <Button
                    variant="contained"
                    startIcon={<GroupAddIcon />}
                    onClick={() => handleCreateTeamInGroup(g.id)}
                    disabled={busy || !draft.trim()}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 700,
                      whiteSpace: 'nowrap',
                      bgcolor: '#c9a227',
                      color: '#1a1a1a',
                      '&:hover': { bgcolor: '#b89220' },
                      '&.Mui-disabled': { bgcolor: 'action.disabledBackground' },
                    }}
                  >
                    Crear y añadir
                  </Button>
                </Stack>
                {openTeamLibrary && onPickFromLibraryForGroup ? (
                  <Button
                    type="button"
                    variant="outlined"
                    size="small"
                    fullWidth
                    startIcon={<BookmarkBorderIcon />}
                    disabled={busy}
                    onClick={() =>
                      openTeamLibrary((item) => onPickFromLibraryForGroup(g.id, item))
                    }
                    sx={{
                      mb: 2,
                      textTransform: 'none',
                      fontWeight: 700,
                      borderColor: 'rgba(15,53,49,0.3)',
                      color: '#0f3531',
                      '&:hover': { borderColor: '#0f6b5c', bgcolor: 'rgba(15,107,92,0.06)' },
                    }}
                  >
                    Elegir de mi biblioteca
                  </Button>
                ) : null}

                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.75 }}>
                  O añadir equipo ya creado
                </Typography>
                <Button
                  fullWidth
                  size="medium"
                  variant="outlined"
                  endIcon={<KeyboardArrowDownIcon />}
                  onClick={(e) => setAddMenu({ anchor: e.currentTarget, groupId: g.id })}
                  disabled={busy || availableTeams.length === 0}
                  sx={{
                    justifyContent: 'space-between',
                    textTransform: 'none',
                    fontWeight: 600,
                    py: 1.25,
                    borderColor: 'rgba(15,53,49,0.25)',
                    color: '#0f3531',
                    '&:hover': { borderColor: '#0f6b5c', bgcolor: 'rgba(15,107,92,0.06)' },
                  }}
                >
                  {availableTeams.length === 0
                    ? teams.length === 0
                      ? 'Primero da de alta equipos en la bolsa (arriba)'
                      : 'Todos los equipos ya están en algún grupo'
                    : `Elegir de la bolsa (${availableTeams.length} disponibles)…`}
                </Button>
                <Menu
                  anchorEl={addMenu.anchor}
                  open={Boolean(addMenu.anchor) && addMenu.groupId === g.id}
                  onClose={closeAddMenu}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                  PaperProps={{ sx: { minWidth: addMenu.anchor?.clientWidth || 280, maxHeight: 320 } }}
                >
                  {addMenu.groupId === g.id &&
                    availableTeams.map((t) => (
                      <MenuItem
                        key={t.id}
                        onClick={() => {
                          closeAddMenu();
                          handleAddTeamToGroup(g.id, t.id);
                        }}
                        sx={{ gap: 1 }}
                      >
                        <Avatar src={t.shield || undefined} sx={{ width: 28, height: 28 }} variant="rounded">
                          {!t.shield ? (t.name || '?').charAt(0) : null}
                        </Avatar>
                        {t.name}
                      </MenuItem>
                    ))}
                </Menu>
              </Box>
            </Paper>
          );
        })}
        {groups.length === 0 && (
          <Typography color="text.secondary" sx={{ py: 2 }}>
            Aún no hay grupos. Crea el primero con el formulario de arriba.
          </Typography>
        )}
      </Stack>
    </>
  );
}
